import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { magazinesStyleSpotlightContentItemContent$key } from "scmp-app/queries/__generated__/magazinesStyleSpotlightContentItemContent.graphql";

import { Backdrop, Container, Content, Cover, Divider, ReadMore, Section, Title } from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: magazinesStyleSpotlightContentItemContent$key;
};

export const ContentItemMagazinesStyleSpotlight: FunctionComponentWithRef<
  Props,
  HTMLDivElement
> = ({ className, ref, reference }) => {
  const data = useFragment(
    graphql`
      fragment magazinesStyleSpotlightContentItemContent on Content {
        ...entityLink
        ... on Article {
          sectionsV2 {
            value {
              ... on Section {
                name
              }
            }
          }
        }
        ...hooksContentItemProvidersContent
          @arguments(withCoverImage: true, withCoverImageSize800x1200: true, withHeadline: true)
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(data);

  const cover = providers.coverImage({
    responsiveVariants: {
      desktopUp: "size800x1200",
      mobileUp: "size800x1200",
      tabletUp: "size800x1200",
    },
  });

  const section = last(first(data.sectionsV2)?.value)?.name;

  const title = providers.headline({ preferSocialHeadline: true });

  return (
    <Container className={className} ref={ref}>
      <Cover>
        {cover}
        <Backdrop />
        <Content>
          <Section>{section}</Section>
          <Title reference={data}>{title}</Title>
          <Divider />
          <ReadMore>read more</ReadMore>
        </Content>
      </Cover>
    </Container>
  );
};

ContentItemMagazinesStyleSpotlight.displayName = "ContentItemMagazinesStyleSpotlight";
